import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BarChart3, Calendar, CreditCard, Eye,
  FileText, MapPin, RefreshCw, Send, Users, Video,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import DealHeader from '@/components/deal/DealHeader';
import useDealFlow from '@/hooks/useDealFlow';
import {
  DEAL_STAGES_V2,
  formatDollars,
  getCurrentCreativeForBusiness,
  getLatestProposalForBusiness,
  isMockGenerationEnabled,
} from '@/components/deal/mockDealData';
import { buildReportNarrative, reportMetricsFromSnapshot } from '@/services/sellerFlowMappers';

export default function CampaignStatus() {
  const { orgSlug, dealId } = useParams();
  const navigate = useNavigate();
  const deal = useDealFlow();
  const businessId = deal.activeBusinessId || deal.businessProfile?.id;
  const proposal = deal.proposal || getLatestProposalForBusiness(deal.proposals?.[businessId] || []);
  const creativePreview = getCurrentCreativeForBusiness(deal, businessId, { mockGenerationEnabled: isMockGenerationEnabled() });
  const reportSummary = deal.reportSummaryByBusiness?.[businessId] || deal.campaign?.reportSummary || null;
  const latestSnapshot = deal.latestReportSnapshotByBusiness?.[businessId] || deal.campaign?.latestSnapshot || null;
  const metrics = reportMetricsFromSnapshot(latestSnapshot, reportSummary, deal.campaign);

  useEffect(() => {
    deal.setCurrentStage('close');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!businessId) return;
    deal.loadCampaignForBusiness(businessId).catch(() => {});
  }, [businessId, deal.loadCampaignForBusiness]);

  if (!proposal) {
    return (
      <>
        <DealHeader
          businessName={deal.businessProfile?.name}
          currentStage="close"
          stages={DEAL_STAGES_V2}
          onBack={() => navigate(`/org/${orgSlug}/leads`)}
        />
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6 text-center">
              <h1 className="text-2xl font-semibold text-slate-950">Campaign live</h1>
              <p className="mt-2 text-sm text-slate-600">
                Campaign context is unavailable because the proposal record is missing.
              </p>
              <Button onClick={() => navigate(`/org/${orgSlug}/leads`)} className="mt-5 rounded-xl bg-coral text-white hover:bg-coral/90">
                Return to My Work
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const spendPercent = Math.round((metrics.spend / Math.max(metrics.budget_cap, 1)) * 100);
  const deliveryHeadline = buildReportNarrative(reportSummary, deal.campaign, deal.businessProfile?.name);
  const launchedLabel = deal.campaign?.launched_at
    ? new Date(deal.campaign.launched_at).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
    : 'Current session';
  const targetingLabel = proposal?.target_area?.label
    || deal.selectedPackage?.targetAddress
    || (deal.businessProfile?.name ? `Around ${deal.businessProfile.name}` : 'Not set');
  const renewalRecommended = metrics.days_remaining <= 10;
  const recommendationTitle = renewalRecommended ? 'Start the renewal conversation' : 'Send a mid-flight update';
  const recommendationBody = renewalRecommended
    ? `This flight has ${metrics.days_remaining} days left. Use the live delivery data to line up the next offer before performance drops out of view.`
    : 'Delivery is still in a healthy window. Share progress now so the buyer stays engaged before the next decision point.';
  const flightLabel = proposal?.flight?.label || `${Math.max(1, Math.round((new Date(metrics.end_date).getTime() - new Date(metrics.start_date).getTime()) / (1000 * 60 * 60 * 24 * 7)))} weeks`;

  return (
    <>
      <DealHeader
        businessName={deal.businessProfile?.name}
        currentStage="close"
        stages={DEAL_STAGES_V2}
        onBack={() => navigate(`/org/${orgSlug}/leads`)}
      />

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-8">
          <div>
            <Badge variant="outline" className="rounded-full border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] text-emerald-700">
              Campaign live
            </Badge>
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              {deal.businessProfile?.name || 'This campaign'} is in delivery
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
              {deliveryHeadline || `Launched from ${proposal?.packageName || 'the accepted proposal'} on ${launchedLabel}. Use this page for pacing, reporting, and renewal follow-up.`}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="grid gap-4 p-5 sm:grid-cols-3">
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Delivery status</p>
                <p className="mt-2 text-lg font-semibold text-slate-950">{reportSummary?.delivery_status?.replace(/_/g, ' ') || 'Live and pacing normally'}</p>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Budget used</p>
                <p className="mt-2 text-lg font-semibold text-slate-950">{spendPercent}%</p>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Days remaining</p>
                <p className="mt-2 text-lg font-semibold text-slate-950">{metrics.days_remaining}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <div className="grid xl:grid-cols-[minmax(0,1fr)_340px]">
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Launch context</p>
                      <h2 className="mt-3 text-xl font-semibold text-slate-950">Built from the accepted commercial plan</h2>
                      <p className="mt-1 text-sm leading-6 text-slate-600">Proposal, billing, creative, and targeting are all set for the current flight.</p>
                    </div>
                    <Badge className="shrink-0 border-0 bg-emerald-50 text-emerald-600">Live</Badge>
                  </div>
                  <div className="mt-6 space-y-3">
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2 text-sm text-slate-500">
                        <FileText className="h-3.5 w-3.5 text-blue-600" />
                        Proposal
                      </span>
                      <span className="min-w-0 text-right text-sm font-medium text-slate-950">
                        {proposal?.packageName || 'Campaign proposal'} · v{proposal?.version || deal.campaign?.proposalVersion || 1}
                      </span>
                    </div>
                    <div className="h-px bg-slate-100" />
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2 text-sm text-slate-500">
                        <CreditCard className="h-3.5 w-3.5 text-emerald-600" />
                        Billing
                      </span>
                      <span className="min-w-0 text-right text-sm font-medium text-slate-950">
                        {deal.agreement?.payment_method_status === 'confirmed' ? 'Payment method on file' : 'Unavailable'}
                      </span>
                    </div>
                    <div className="h-px bg-slate-100" />
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2 text-sm text-slate-500">
                        <Calendar className="h-3.5 w-3.5 text-slate-500" />
                        Flight
                      </span>
                      <span className="min-w-0 text-right text-sm font-medium text-slate-950">{flightLabel}</span>
                    </div>
                    <div className="h-px bg-slate-100" />
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2 text-sm text-slate-500">
                        <Video className="h-3.5 w-3.5 text-coral" />
                        Creative
                      </span>
                      <span className="min-w-0 text-right text-sm font-medium text-slate-950">{creativePreview.label}</span>
                    </div>
                    <div className="h-px bg-slate-100" />
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2 text-sm text-slate-500">
                        <MapPin className="h-3.5 w-3.5 text-coral" />
                        Targeting
                      </span>
                      <span className="min-w-0 text-right text-sm font-medium text-slate-950">{targetingLabel}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 bg-slate-50/70 p-6 xl:border-t-0 xl:border-l">
                  <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Live performance</p>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                        <Users className="h-3.5 w-3.5 text-coral" />
                        Households
                      </p>
                      <p className="mt-3 text-3xl font-semibold text-slate-950">{metrics.households_reached.toLocaleString()}</p>
                      <p className="mt-1 text-sm text-slate-500">Reached so far</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                        <Eye className="h-3.5 w-3.5 text-blue-600" />
                        Ad views
                      </p>
                      <p className="mt-3 text-3xl font-semibold text-slate-950">{metrics.impressions.toLocaleString()}</p>
                      <p className="mt-1 text-sm text-slate-500">Delivered so far</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                        <CreditCard className="h-3.5 w-3.5 text-emerald-600" />
                        Spend
                      </p>
                      <p className="mt-3 text-3xl font-semibold text-slate-950">{formatDollars(metrics.spend)}</p>
                      <p className="mt-1 text-sm text-slate-500">Spent to date</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                        <RefreshCw className="h-3.5 w-3.5 text-coral" />
                        Remaining
                      </p>
                      <p className="mt-3 text-3xl font-semibold text-slate-950">{metrics.days_remaining}</p>
                      <p className="mt-1 text-sm text-slate-500">Days left in flight</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-slate-950">Budget pacing</h2>
                    <p className="mt-1 text-sm text-slate-600">Live spend versus total approved budget.</p>
                  </div>
                  <span className="text-sm font-semibold text-slate-950">
                    {formatDollars(metrics.spend)} / {formatDollars(metrics.budget_cap)}
                  </span>
                </div>
                <Progress value={spendPercent} className="mt-4 h-2 bg-slate-100 [&>[data-slot=progress-indicator]]:bg-coral" />
                <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
                  <span>{spendPercent}% of budget used</span>
                  <span>{metrics.days_remaining} days remaining</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="grid gap-6 p-5 xl:grid-cols-[minmax(0,1fr)_320px]">
              <div>
                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Commercial next step</p>
                <h2 className="mt-3 text-xl font-semibold text-slate-950">{recommendationTitle}</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  {recommendationBody}
                </p>
              </div>

              <div className="space-y-5">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Campaign timeline</p>
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-slate-500">Start</span>
                      <span className="text-right text-sm font-semibold text-slate-950">{metrics.start_date}</span>
                    </div>
                    <div className="h-px bg-slate-200" />
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-slate-500">End</span>
                      <span className="text-right text-sm font-semibold text-slate-950">{metrics.end_date}</span>
                    </div>
                    <div className="h-px bg-slate-200" />
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-slate-500">Launched</span>
                      <span className="text-right text-sm font-semibold text-slate-950">{launchedLabel}</span>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3">
                  <Button variant="outline" onClick={() => toast.success('Performance update sent')} className="h-11 rounded-xl bg-white">
                    <Send className="h-4 w-4" />
                    Send update
                  </Button>
                  <Button variant="outline" onClick={() => navigate(`/org/${orgSlug}/deal/${dealId}/report`)} className="h-11 rounded-xl bg-white">
                    <BarChart3 className="h-4 w-4" />
                    Full report
                  </Button>
                  <Button onClick={() => toast.info('Renewal workflow not implemented yet')} className="h-11 rounded-xl bg-coral text-white hover:bg-coral/90">
                    <RefreshCw className="h-4 w-4" />
                    Renew or extend
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
